import React, { useState, useEffect, useRef } from 'react';
import {
  Bell, Plus, Edit2, Trash2, Send, X, Clock,
  ChevronDown, ChevronUp, CheckCircle, AlertCircle,
  ToggleLeft, ToggleRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { emailNotificationAPI } from '../../services/api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ROLE_OPTIONS = [
  { value: 'teacher',     label: 'Teacher' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'management',  label: 'Management' },
  { value: 'admin',       label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const FREQUENCY_OPTIONS = [
  { value: 'once',    label: 'Once' },
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PLACEHOLDERS = ['{RecipientName}', '{Date}', '{DayOfWeek}', '{School}', '{Module}'];

// ── Module-specific trigger options ──────────────────────
// Each option declares which backend fields to use, keeping the UI simple.
const MODULE_TRIGGERS = {
  learningTrackers: [
    { value: 'not_filled',  label: 'Not Filled',
      desc: 'Daily — sends to teachers who haven\'t filled their tracker entries',
      type: 'status',   filter: 'not_completed', dir: null, showDays: false },
    { value: 'pre_task',    label: 'Pre-Task Reminder',
      desc: 'Scheduled days before the deadline — sends to teachers with pending entries',
      type: 'schedule', filter: 'not_completed', dir: null, showDays: true },
    { value: 'post_task',   label: 'Post-Task Follow-up',
      desc: 'After the deadline — sends to teachers who still haven\'t filled entries',
      type: 'status',   filter: 'not_completed', dir: null, showDays: false },
    { value: 'completed',   label: 'Completion Notice',
      desc: 'Daily — sends to teachers who have completed all tracker entries',
      type: 'status',   filter: 'completed', dir: null, showDays: false },
    { value: 'scheduled',   label: 'Scheduled',
      desc: 'Sends to all selected roles on the chosen days, regardless of status',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
  homework: [
    { value: 'not_submitted', label: 'Not Submitted',
      desc: 'Daily — sends to teachers with pending homework submissions',
      type: 'status',   filter: 'not_completed', dir: null, showDays: false },
    { value: 'overdue',       label: 'Overdue',
      desc: 'Daily — sends to teachers with homework past the due date and still incomplete',
      type: 'status',   filter: 'overdue', dir: null, showDays: false },
    { value: 'pre_due',       label: 'Pre Due-Date Reminder',
      desc: 'N days before due date — targets teachers whose homework is still incomplete',
      type: 'timeline', filter: 'not_completed', dir: 'before', showDays: false },
    { value: 'post_due',      label: 'Post Due-Date Follow-up',
      desc: 'N days after due date — follows up with teachers who still haven\'t submitted',
      type: 'timeline', filter: 'not_completed', dir: 'after', showDays: false },
    { value: 'scheduled',     label: 'Scheduled',
      desc: 'Sends on the chosen days to all selected roles',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
  newsletter: [
    { value: 'draft_pending', label: 'Draft Pending',
      desc: 'Daily — sends to teachers with unpublished newsletters',
      type: 'status',   filter: 'pending', dir: null, showDays: false },
    { value: 'overdue',       label: 'Overdue',
      desc: 'Daily — sends to teachers whose publish date has passed but newsletter is still draft',
      type: 'status',   filter: 'overdue', dir: null, showDays: false },
    { value: 'pre_publish',   label: 'Before Publish Date',
      desc: 'N days before the publish date — advance reminder',
      type: 'timeline', filter: 'all', dir: 'before', showDays: false },
    { value: 'scheduled',     label: 'Scheduled',
      desc: 'Sends on the chosen days to all selected roles',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
  toddleLessonPlan: [
    { value: 'pending',   label: 'Pending',
      desc: 'Daily — sends to teachers with pending lesson plans',
      type: 'status',   filter: 'pending', dir: null, showDays: false },
    { value: 'pre_task',  label: 'Pre-Task Reminder',
      desc: 'Scheduled days — reminds teachers to complete lesson plans ahead of time',
      type: 'schedule', filter: 'pending', dir: null, showDays: true },
    { value: 'completed', label: 'Completion Notice',
      desc: 'Daily — sends to teachers who have completed their lesson plans',
      type: 'status',   filter: 'completed', dir: null, showDays: false },
    { value: 'scheduled', label: 'Scheduled',
      desc: 'Sends on the chosen days to all selected roles',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
  teacherObservations: [
    { value: 'draft',     label: 'Draft Pending',
      desc: 'Daily — sends to teachers with observations still in draft',
      type: 'status',   filter: 'pending', dir: null, showDays: false },
    { value: 'scheduled', label: 'Scheduled',
      desc: 'Sends on the chosen days to all selected roles',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
  morningActivity: [
    { value: 'incomplete', label: 'Not Completed',
      desc: 'Daily — sends when morning activities are marked incomplete',
      type: 'status',   filter: 'not_completed', dir: null, showDays: false },
    { value: 'pre_task',   label: 'Pre-Activity Reminder',
      desc: 'N days before the activity date',
      type: 'timeline', filter: 'all', dir: 'before', showDays: false },
    { value: 'scheduled',  label: 'Scheduled',
      desc: 'Sends on the chosen days to all selected roles',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
  grievances: [
    { value: 'new_grievance', label: 'New Grievance',
      desc: 'Sends immediately when a new grievance is raised — notifies the assigned staff',
      type: 'status',   filter: 'new', dir: null, showDays: false },
    { value: 'unresolved',    label: 'Unresolved Alert',
      desc: 'Daily — sends when grievances remain unresolved beyond the configured number of days',
      type: 'status',   filter: 'overdue', dir: null, showDays: false },
    { value: 'status_change', label: 'Status Update',
      desc: 'Sends when a grievance status changes (e.g. In Progress → Resolved)',
      type: 'status',   filter: 'all', dir: null, showDays: false },
    { value: 'scheduled',     label: 'Scheduled Summary',
      desc: 'Sends a grievance summary on the chosen days to all selected roles',
      type: 'schedule', filter: 'all', dir: null, showDays: true },
  ],
};

const DEFAULT_TRIGGERS = [
  { value: 'scheduled', label: 'Scheduled',
    desc: 'Sends on the chosen days to all selected roles',
    type: 'schedule', filter: 'all', dir: null, showDays: true },
];

// Detect which trigger option a saved rule maps to
function detectTriggerValue(rule, module) {
  const opts = MODULE_TRIGGERS[module] || DEFAULT_TRIGGERS;
  if (rule.trigger_type === 'schedule') {
    const rf = rule.recipient_filter || 'all';
    return opts.find(o => o.type === 'schedule' && o.filter === rf)?.value
        || opts.find(o => o.type === 'schedule')?.value
        || opts[0].value;
  }
  if (rule.trigger_type === 'timeline') {
    const dir = rule.timeline_direction || 'before';
    return opts.find(o => o.type === 'timeline' && o.dir === dir)?.value || opts[0].value;
  }
  // status
  const rf = rule.recipient_filter || 'all';
  return opts.find(o => o.type === 'status' && o.filter === rf)?.value || opts[0].value;
}

const EMPTY_FORM = {
  rule_name:     '',
  trigger:       '',
  timeline_days: '3',
  schedule_days: 'Mon,Tue,Wed,Thu,Fri',
  schedule_time: '08:00',
  frequency:     'daily',
  roles:         ['teacher', 'coordinator'],
  extra_emails:  '',
  subject:       '',
  body:          '',
  is_active:     true,
};

export default function EmailNotificationSetup({ module, moduleLabel, yearId }) {
  const triggers = MODULE_TRIGGERS[module] || DEFAULT_TRIGGERS;

  const [rules,       setRules]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null);  // null | 'add' | { mode:'edit', id }
  const [form,        setForm]        = useState({ ...EMPTY_FORM, trigger: triggers[0].value });
  const [saving,      setSaving]      = useState(false);
  const [testing,     setTesting]     = useState(null);
  const [expanded,    setExpanded]    = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  const bodyRef = useRef(null);

  useEffect(() => { loadRules(); }, [module, yearId]); // eslint-disable-line

  async function loadRules() {
    setLoading(true);
    try {
      const res = await emailNotificationAPI.getRules({ module, academic_year_id: yearId });
      setRules(res.data);
    } catch { toast.error('Failed to load rules'); }
    finally  { setLoading(false); }
  }

  const selectedTrigger = triggers.find(t => t.value === form.trigger) || triggers[0];

  function openAdd() {
    setForm({ ...EMPTY_FORM, trigger: triggers[0].value, roles: ['teacher', 'coordinator'] });
    setModal('add');
  }

  function openEdit(rule) {
    setForm({
      rule_name:     rule.rule_name          || '',
      trigger:       detectTriggerValue(rule, module),
      timeline_days: rule.timeline_days != null ? String(rule.timeline_days) : '3',
      schedule_days: rule.schedule_days      || 'Mon,Tue,Wed,Thu,Fri',
      schedule_time: rule.schedule_time      || '08:00',
      frequency:     rule.frequency          || 'daily',
      roles:         (rule.recipient_roles || '').split(',').map(r => r.trim()).filter(Boolean),
      extra_emails:  rule.recipient_emails   || '',
      subject:       rule.subject            || '',
      body:          rule.body               || '',
      is_active:     rule.is_active          !== 0,
    });
    setModal({ mode: 'edit', id: rule.id });
  }

  // Day toggles
  const selectedDays = (form.schedule_days || '').split(',').map(d => d.trim()).filter(Boolean);
  function toggleDay(d) {
    const next = selectedDays.includes(d) ? selectedDays.filter(x => x !== d) : [...selectedDays, d];
    setForm(f => ({ ...f, schedule_days: next.join(',') }));
  }

  // Role toggles
  function toggleRole(r) {
    setForm(f => {
      const next = f.roles.includes(r) ? f.roles.filter(x => x !== r) : [...f.roles, r];
      return { ...f, roles: next };
    });
  }

  function appendPlaceholder(ph) {
    setForm(f => ({ ...f, body: f.body + ph }));
    bodyRef.current?.focus();
  }

  function buildPayload() {
    const trig = selectedTrigger;
    return {
      module,
      academic_year_id:    yearId,
      rule_name:           form.rule_name.trim(),
      subject:             form.subject.trim(),
      body:                form.body.trim(),
      is_active:           form.is_active ? 1 : 0,
      schedule_time:       form.schedule_time,
      frequency:           form.frequency,
      recipient_roles:     form.roles.join(','),
      recipient_emails:    form.extra_emails,
      recipient_filter:    trig.filter,
      trigger_type:        trig.type,
      timeline_days:       trig.type === 'timeline' ? (Number(form.timeline_days) || 3) : null,
      timeline_direction:  trig.type === 'timeline' ? (trig.dir || 'before') : null,
      schedule_days:       trig.showDays ? form.schedule_days : 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
      condition_statuses:  '',
      escalation_level:    0,
      escalation_group:    '',
      recipient_grades:    '',
    };
  }

  async function handleSave() {
    if (!form.rule_name.trim()) return toast.error('Rule name is required');
    if (!form.subject.trim())   return toast.error('Email subject is required');
    if (!form.body.trim())      return toast.error('Email body is required');
    if (selectedTrigger.type === 'timeline' && !form.timeline_days)
      return toast.error('Days field is required for this trigger type');

    setSaving(true);
    try {
      const payload = buildPayload();
      if (modal === 'add') {
        await emailNotificationAPI.createRule(payload);
        toast.success('Rule created');
      } else {
        await emailNotificationAPI.updateRule(modal.id, payload);
        toast.success('Rule updated');
      }
      setModal(null);
      loadRules();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this notification rule?')) return;
    try {
      await emailNotificationAPI.deleteRule(id);
      toast.success('Deleted');
      setRules(r => r.filter(x => x.id !== id));
    } catch { toast.error('Failed to delete'); }
  }

  async function handleToggle(id) {
    try {
      const res = await emailNotificationAPI.toggleRule(id);
      setRules(r => r.map(x => x.id === id ? res.data : x));
    } catch { toast.error('Failed to toggle'); }
  }

  async function handleTest(id) {
    setTesting(id);
    try {
      const res = await emailNotificationAPI.testSend(id);
      toast.success(res.data.message || 'Test email sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test failed');
    } finally { setTesting(null); }
  }

  async function toggleLogs(id) {
    if (expanded[id] !== undefined) {
      setExpanded(e => { const n = { ...e }; delete n[id]; return n; });
      return;
    }
    setLoadingLogs(l => ({ ...l, [id]: true }));
    try {
      const res = await emailNotificationAPI.getLogs({ rule_id: id });
      setExpanded(e => ({ ...e, [id]: res.data }));
    } catch { toast.error('Failed to load logs'); }
    finally { setLoadingLogs(l => ({ ...l, [id]: false })); }
  }

  function ruleSummary(rule) {
    const trig = (MODULE_TRIGGERS[module] || DEFAULT_TRIGGERS).find(t =>
      t.value === detectTriggerValue(rule, module)) || {};
    const time = rule.schedule_time || '08:00';
    if (trig.type === 'timeline') {
      const days = rule.timeline_days ?? '?';
      const dir  = rule.timeline_direction === 'after' ? 'after' : 'before';
      return `${trig.label} · ${days}d ${dir} · ${time}`;
    }
    if (trig.type === 'status') return `${trig.label} · Daily ${time}`;
    const days = (rule.schedule_days || '').split(',').join(' ');
    return `${days || 'All days'} · ${time}`;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-card">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Bell size={17} className="text-primary-600" />
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Email Notification Rules</h3>
            <p className="text-xs text-slate-400">{moduleLabel} · {rules.length} rule{rules.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-lg transition-colors">
          <Plus size={13} /> Add Rule
        </button>
      </div>

      {/* Rule list */}
      <div className="divide-y divide-slate-50">
        {loading ? (
          <p className="py-8 text-center text-slate-400 text-sm">Loading…</p>
        ) : rules.length === 0 ? (
          <div className="py-10 text-center">
            <Bell size={30} className="mx-auto text-slate-200 mb-2" />
            <p className="text-slate-400 text-sm">No notification rules yet</p>
            <button onClick={openAdd} className="mt-2 text-primary-600 text-xs font-medium hover:underline">
              Add the first rule
            </button>
          </div>
        ) : rules.map(rule => (
          <div key={rule.id} className="px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={() => handleToggle(rule.id)} className="flex-shrink-0 text-slate-300 hover:text-primary-600 transition-colors">
                {rule.is_active ? <ToggleRight size={22} className="text-primary-600" /> : <ToggleLeft size={22} />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{rule.rule_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{ruleSummary(rule)}</p>
              </div>
              <div className="flex items-center gap-0.5">
                <button onClick={() => handleTest(rule.id)} disabled={testing === rule.id}
                  title="Send test email"
                  className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-40">
                  {testing === rule.id
                    ? <span className="block w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    : <Send size={13} />}
                </button>
                <button onClick={() => toggleLogs(rule.id)} title="History"
                  className={`p-1.5 rounded-lg transition-colors ${expanded[rule.id] !== undefined ? 'bg-slate-100 text-slate-700' : 'hover:bg-slate-50 text-slate-400 hover:text-slate-600'}`}>
                  {loadingLogs[rule.id]
                    ? <span className="block w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    : expanded[rule.id] !== undefined ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                <button onClick={() => openEdit(rule)} title="Edit"
                  className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors">
                  <Edit2 size={13} />
                </button>
                <button onClick={() => handleDelete(rule.id)} title="Delete"
                  className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>

            {expanded[rule.id] !== undefined && (
              <div className="mt-2 ml-8 bg-slate-50 rounded-lg border border-slate-100 p-2.5 space-y-1.5">
                {expanded[rule.id].length === 0
                  ? <p className="text-xs text-slate-400 text-center">No history yet</p>
                  : expanded[rule.id].map(log => (
                    <div key={log.id} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        {log.status === 'success'
                          ? <CheckCircle size={11} className="text-emerald-500" />
                          : <AlertCircle size={11} className="text-red-500" />}
                        <span className="text-slate-500">
                          {new Date(log.sent_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                        </span>
                        {log.error_message && <span className="text-red-500 truncate max-w-xs">{log.error_message}</span>}
                      </div>
                      <span className={`font-medium ${log.status === 'success' ? 'text-emerald-600' : 'text-red-500'}`}>
                        {log.status === 'success' ? `${log.recipients_count} sent` : 'Failed'}
                      </span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Modal ── */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col">

            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Bell size={16} className="text-primary-600" />
                <h3 className="font-semibold text-slate-800 text-sm">
                  {modal === 'add' ? 'New Notification Rule' : 'Edit Notification Rule'}
                </h3>
              </div>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={15} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">

              {/* Rule name + active */}
              <div className="flex items-center gap-3">
                <input type="text" value={form.rule_name}
                  onChange={e => setForm(f => ({ ...f, rule_name: e.target.value }))}
                  placeholder="Rule name (e.g. Weekly Tracker Reminder)"
                  className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}>
                  {form.is_active
                    ? <ToggleRight size={26} className="text-primary-600" />
                    : <ToggleLeft  size={26} className="text-slate-300" />}
                </button>
                <span className="text-xs text-slate-400 -ml-1 w-12">{form.is_active ? 'Active' : 'Paused'}</span>
              </div>

              {/* ─ Trigger ─ */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">When to Send</p>
                <div className="space-y-2">
                  {triggers.map(t => (
                    <label key={t.value}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        form.trigger === t.value
                          ? 'border-primary-400 bg-primary-50'
                          : 'border-slate-100 hover:border-slate-200 bg-white'
                      }`}>
                      <input type="radio" name="trigger" value={t.value} checked={form.trigger === t.value}
                        onChange={() => setForm(f => ({ ...f, trigger: t.value }))}
                        className="mt-0.5 text-primary-600 focus:ring-primary-500" />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${form.trigger === t.value ? 'text-primary-700' : 'text-slate-700'}`}>
                          {t.label}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{t.desc}</p>

                        {/* Conditional: timeline days */}
                        {form.trigger === t.value && t.type === 'timeline' && (
                          <div className="mt-2.5 flex items-center gap-2">
                            <input type="number" min={1} max={30} value={form.timeline_days}
                              onChange={e => setForm(f => ({ ...f, timeline_days: e.target.value }))}
                              className="w-16 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                            <span className="text-xs text-slate-500">days {t.dir === 'after' ? 'after' : 'before'} due date</span>
                          </div>
                        )}

                        {/* Conditional: day checkboxes */}
                        {form.trigger === t.value && t.showDays && (
                          <div className="mt-2.5 flex gap-1.5 flex-wrap">
                            {DAYS.map(d => (
                              <button key={d} type="button" onClick={() => toggleDay(d)}
                                className={`px-2 py-0.5 rounded-md text-xs font-medium border transition-all ${
                                  selectedDays.includes(d)
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-primary-300'
                                }`}>{d}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* ─ Time + Frequency ─ */}
              <div className="flex items-start gap-4">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    <Clock size={11} className="inline mr-1" />Send at
                  </p>
                  <input type="time" value={form.schedule_time}
                    onChange={e => setForm(f => ({ ...f, schedule_time: e.target.value }))}
                    className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Repeat</p>
                  <div className="flex gap-1.5">
                    {FREQUENCY_OPTIONS.map(f => (
                      <button key={f.value} type="button"
                        onClick={() => setForm(frm => ({ ...frm, frequency: f.value }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          form.frequency === f.value
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300'
                        }`}>{f.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ─ Recipients ─ */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Recipients</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
                  {ROLE_OPTIONS.map(r => (
                    <label key={r.value} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={form.roles.includes(r.value)}
                        onChange={() => toggleRole(r.value)}
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
                      <span className="text-xs text-slate-700">{r.label}</span>
                    </label>
                  ))}
                </div>
                <input type="text" value={form.extra_emails}
                  onChange={e => setForm(f => ({ ...f, extra_emails: e.target.value }))}
                  placeholder="Extra emails (optional) — comma-separated"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>

              {/* ─ Email template ─ */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Email Template</p>
                <input type="text" value={form.subject}
                  onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="Subject — e.g. Reminder: {Module} entry pending on {Date}"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 mb-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <div className="flex gap-1.5 flex-wrap mb-2">
                  {PLACEHOLDERS.map(ph => (
                    <button key={ph} type="button" onClick={() => appendPlaceholder(ph)}
                      className="px-2 py-0.5 text-xs bg-slate-100 hover:bg-primary-100 text-slate-600 hover:text-primary-700 rounded border border-slate-200 hover:border-primary-300 transition-colors">
                      {ph}
                    </button>
                  ))}
                  <span className="text-xs text-slate-400 self-center">← click to insert</span>
                </div>
                <textarea ref={bodyRef} value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  rows={5}
                  placeholder={`Dear {RecipientName},\n\nThis is a reminder to fill your {Module} entries by {Date}.\n\nRegards,\n{School}`}
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y font-mono" />
              </div>

            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setModal(null)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-60">
                {saving
                  ? <><span className="block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
                  : modal === 'add' ? 'Create Rule' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
